import { CdkScrollable } from "@angular/cdk/scrolling";
import { NgFor, NgIf, SlicePipe } from "@angular/common";
import {
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  forwardRef,
  viewChild,
} from "@angular/core";
import { ExtendedModule } from "@angular/flex-layout/extended";
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { MatAutocomplete, MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { MatOption } from "@angular/material/core";
import { MatIcon } from "@angular/material/icon";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin } from "rxjs";
import { Subscription } from "rxjs/internal/Subscription";
import { LocalStorageService } from "src/app/service/localStorage.service";
import { EmployeeService } from "src/app/shared/services/employee.service";
import { SearchResult } from "../../models/SearchResults";
import { InitialsPipe } from "../../routes/pipes/Initials.pipe";
import { ClearSearchService } from "../../service/clear-search-service.service";
import { NavigationService } from "../../service/navigation.service";
import { SearchService } from "../../service/search.service";
import { environment } from "src/environments/environment";

const LOCAL_STORAGE_KEY = "recentSearches";

@Component({
  selector: "app-common-search",
  templateUrl: "./common-search.component.html",
  styleUrls: ["./common-search.component.scss"],
  imports: [
    MatAutocomplete,
    NgIf,
    NgFor,
    MatOption,
    MatIcon,
    ExtendedModule,
    CdkScrollable,
    FormsModule,
    MatAutocompleteTrigger,
    ReactiveFormsModule,
    forwardRef(() => AutofocusDirective),
    SlicePipe,
    InitialsPipe,
  ],
})
export class CommonSearchComponent implements OnInit, OnDestroy {
  searchValue = "";
  myControl: UntypedFormControl = new UntypedFormControl();
  results: SearchResult[];
  @Input() searchActive: boolean = false;
  @Output() searchActiveChange = new EventEmitter<boolean>();
  readonly autocomplete = viewChild(MatAutocompleteTrigger);
  clearDataSubscription: Subscription;
  readonly useSearchV2Service = environment.useSearchV2Service;
  photoLoadErrors = new Set<string>();

  constructor(
    private router: Router,
    public navService: NavigationService,
    public route: ActivatedRoute,
    public searchService: SearchService,
    private clearSearchService: ClearSearchService,
    private localStorageService: LocalStorageService,
    private employeeService: EmployeeService
  ) {}

  ngOnDestroy(): void {
    if (this.clearDataSubscription) {
      this.clearDataSubscription.unsubscribe();
    }
  }

  ngOnInit(): void {
    this.searchValue = "";
    this.setRecentSearches();
    // will handle clearing the data from the search bar when ngOnDestroy called in search results
    this.clearDataSubscription = this.clearSearchService.clearSearchEvent.subscribe((condition) => {
      if (condition) {
        this.clearSearch();
      }
    });
  }

  displayFn(e): string {
    return e?.name;
  }

  valuechange(e): any {
    if (
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight"
    ) {
      return;
    }

    const filterValue = (e.target as HTMLInputElement).value;

    if (filterValue?.length) {
      // Clear photo errors for new search
      this.photoLoadErrors.clear();

      if (this.useSearchV2Service) {
        const query = {
          q: filterValue,
          pageSize: "10",
          pageNumber: "1",
          returnFields: "name,title,workalignment,office,photourl,type",
        };
        this.searchService.getAdvancedSearchV2Results(query).subscribe((results) => {
          this.results = results.searchResults;
        });
      } else {
        const query = {
          q: filterValue,
          pageSize: "10",
          pageNumber: "1",
          sort: "null",
          type: "person",
          returnFields: "name,title,workalignment,location,photourl,type",
        };
        this.searchService.getAdvancedSearchResults(query).subscribe((results) => {
          this.results = results.searchResults;
        });
      }
    } else {
      this.setRecentSearches();
    }
  }

  resultIcon(rt): string {
    if (rt == "role") {
      return "assignment";
    } else if (rt == "title") {
      return "assignment_turned_in";
    }
  }

  /* navigate to the selected search input result page */
  searchResultNavigate(result) {
    this.autocomplete().closePanel();
    this.searchValue = "";
    if (result.recordType == "role") {
      this.router.navigate([this.navService.roleLibaryUrl(result.id)]);
    } else if (result.recordType == "title") {
      this.router.navigate([this.navService.titlesUrl(result.id)]);
    } else if (result.recordType == "person") {
      this.addToRecentSearches(result);
      this.clearSearch();
      this.router
        .navigateByUrl("/", { skipLocationChange: true })
        .then(() => this.router.navigate([this.navService.publicProfileUrl(result.employeeId)]));
    } else {
      this.router.navigateByUrl("/", { skipLocationChange: true }).then(() =>
        this.router.navigate([this.navService.searchResultsUrl()], {
          queryParams: { q: result },
        })
      );
    }
  }

  navigateClickAllResults(r) {
    this.autocomplete().closePanel();

    if (r.recordType) {
      this.router.navigate([this.navService.searchResultsUrl()], {
        queryParams: { q: r.name },
      });
    } else {
      this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this.router.navigate([this.navService.searchResultsUrl()], {
          queryParams: { q: r },
        });
      });
    }
  }

  clearSearch() {
    this.searchValue = "";
    this.results = [];
    this.photoLoadErrors.clear();
    this.setRecentSearches();
  }

  retractSearchBar() {
    // Hack to give the Advanced Search link time to navigate before hiding it.
    setTimeout(() => {
      this.searchActive = false;
      this.searchActiveChange.emit(this.searchActive);
    }, 250);
  }

  expandSearchBar() {
    this.searchActive = true;
    this.searchActiveChange.emit(this.searchActive);
  }

  addToRecentSearches(personRecord) {
    personRecord = { ...personRecord, isRecentSearch: true };
    const recentSearchs = this.localStorageService.getItem(LOCAL_STORAGE_KEY);
    if (!recentSearchs || recentSearchs.length === 0) {
      this.localStorageService.setItem(LOCAL_STORAGE_KEY, [personRecord]);
    } else if (!recentSearchs.find((x) => x.employeeId === personRecord.employeeId)) {
      if (recentSearchs.length >= 5) {
        recentSearchs.pop();
        recentSearchs.unshift(personRecord);
        this.localStorageService.setItem(LOCAL_STORAGE_KEY, recentSearchs);
      } else {
        recentSearchs.push(personRecord);
        this.localStorageService.setItem(LOCAL_STORAGE_KEY, recentSearchs);
      }
    }
  }

  removeRecentSearch(event, personRecord) {
    event.stopPropagation();
    event.preventDefault();

    const recentSearchs = this.localStorageService.getItem(LOCAL_STORAGE_KEY);
    const index = recentSearchs.findIndex((x) => x.employeeId === personRecord.employeeId);
    recentSearchs.splice(index, 1);
    this.localStorageService.setItem(LOCAL_STORAGE_KEY, recentSearchs);
    this.setRecentSearches();
  }

  setRecentSearches() {
    this.results = this.localStorageService.getItem(LOCAL_STORAGE_KEY) || [];
    const recentSearchesBios$ = this.results.map((result) =>
      this.employeeService.getBio(result.id)
    );
    forkJoin(recentSearchesBios$).subscribe((bios) => {
      bios.forEach((bio, index) => {
        if (this.results[index] !== undefined || this.results[index] !== null) {
          this.results[index].photoUrl = bio.photo;
        }
      });
    });
  }

  openAutocomplete() {
    const autocomplete = this.autocomplete();
    if (autocomplete) {
      autocomplete.openPanel();
    }
  }
}

@Directive({
  selector: "[appSearchFocus]",
  standalone: true,
})
export class AutofocusDirective {
  constructor(private host: ElementRef) {}

  ngAfterViewInit() {
    this.host.nativeElement.focus();
  }
}
